// Google Consent Mode v2 bridge.
//
// The DEFAULT state (deny everything non-essential) is set in index.html
// BEFORE gtag.js can load — that is what keeps Omega compliant on first paint:
// no analytics/ads storage until the visitor acts. This module only pushes the
// live `update` once we have a ConsentRecord, and lazily injects gtag.js the
// first time any non-essential category is granted (so a pure-reject visitor
// never even downloads Google's tag).
//
// The four Consent Mode v2 signals, and the category each maps to:
//   analytics_storage   ← analytics   (GA4 measurement cookies)
//   ad_storage          ← marketing   (ads / remarketing cookies)
//   ad_user_data        ← marketing   (sending user data to Google for ads)
//   ad_personalization  ← marketing   (personalised / remarketing ads)
// plus functionality_storage + personalization_storage ← functional.

import type { ConsentRecord } from '@shared';

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

type Signal = 'granted' | 'denied';
const sig = (granted: boolean): Signal => (granted ? 'granted' : 'denied');

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') window.gtag(...args);
  else window.dataLayer.push(args);
}

/** Push the live consent state to Google Consent Mode. Safe to call repeatedly
 *  (first visit, returning visit re-hydrate, preference update, withdrawal). */
export function applyConsent(record: ConsentRecord): void {
  const { analytics, marketing, functional } = record.categories;
  gtag('consent', 'update', {
    analytics_storage: sig(analytics),
    ad_storage: sig(marketing),
    ad_user_data: sig(marketing),
    ad_personalization: sig(marketing),
    functionality_storage: sig(functional),
    personalization_storage: sig(functional),
  });
}

let gtagLoaded = false;

/** Inject gtag.js exactly once, and only when a measurement ID is configured.
 *  No-op in environments without VITE_GA_MEASUREMENT_ID (dev, tests). */
export function ensureGtagLoaded(): void {
  if (gtagLoaded || !GA_ID) return;
  gtagLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  // `js` + `config` are buffered into dataLayer until the script initialises.
  gtag('js', new Date());
  gtag('config', GA_ID, { anonymize_ip: true });
}
